import { useContext } from 'react';
import { Button, Tooltip, Breadcrumb, List, Typography } from 'antd';
import Link from 'next/link';
import { isDesktop, isMobile } from 'react-device-detect';
import Image from 'next/image';
import { FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';

import ShopOrderStep from '../components/shop/ShopOrderStep';
import Layout from '~/v2/components/layout/Layout';
import { AuthContext, AuthContextT } from '~/v2/context/AuthProvider';
import { CartContext, CartContextT } from '~/v2/context/CartProvider';
import QuantityBox from '~/v2/components/others/QuantityBox';
import Fallback from '~/v2/components/wrapper/Fallback';
import ConfirmDialog from '~/v2/components/dialog/ConfirmDialog';
import useModal, { ModalHookT } from '~/v2/hooks/useModal';
import { CartItemT } from '~/@customTypes/cart.type';

const Container = dynamic(() => import('../components/other/Container'));

function Cart() {
  const authCtx = useContext<Partial<AuthContextT>>(AuthContext);
  const cartCtx = useContext<Partial<CartContextT>>(CartContext);
  const { formatMessage } = useIntl();
  const singleProductRemoveModal: ModalHookT<{ id: number }> = useModal();
  const emptyCartModal: ModalHookT = useModal();

  return (
    <Layout
      title='Shopping Cart'
      head={
        <>
          <link rel='canonical' href='https://beccato.com.my/cart' />
        </>
      }
    >
      <Container>
        <Breadcrumb separator='>'>
          <Breadcrumb.Item>
            <i className='fas fa-home' />
            <FormattedMessage defaultMessage='Home' id='app.common.home' />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage defaultMessage='Shop' id='app.common.shop' />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage defaultMessage='Cart' id='app.common.cart' />
          </Breadcrumb.Item>
        </Breadcrumb>
        <ShopOrderStep current={1} />

        <Fallback showEmpty={!cartCtx.cart?.items}>
          {isDesktop && (
            <table>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: 150 / 16 + 'em' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className='text-center font-extrabold'>
                    <FormattedMessage id='app.cart.image' defaultMessage='Image' />
                  </th>
                  <th className='text-center font-extrabold'>
                    <FormattedMessage id='app.cart.product_name' defaultMessage='Product Name' />
                  </th>
                  <th className='text-center font-extrabold'>
                    <FormattedMessage id='app.cart.price' defaultMessage='Price' />
                  </th>
                  <th className='text-center font-extrabold'>
                    <FormattedMessage id='app.cart.quantity' defaultMessage='Quantity' />
                  </th>
                  <th className='text-center font-extrabold'>
                    <FormattedMessage id='app.common.total' defaultMessage='Total' />
                  </th>
                  <th>
                    <Tooltip title={<FormattedMessage id='app.cart.clear_all' defaultMessage='Clear cart' />}>
                      <Button
                        onClick={() => {
                          emptyCartModal.onOpen();
                        }}
                        icon={<i className='fal fa-times' />}
                      ></Button>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {cartCtx.cart?.items?.map(
                  (item, index) =>
                    item && (
                      <tr key={index}>
                        <td>
                          <div className='w-50 h-50'>
                            <img
                              src={item.product.base_image.medium_image_url}
                              alt='Product image'
                              className='w-35 h-35 object-contain p-4'
                              loading='lazy'
                            />
                          </div>
                        </td>
                        <td className='text-center'>
                          <Typography.Title level={5} style={{ lineHeight: 1.2, marginBottom: 0 }}>
                            {item.name}
                          </Typography.Title>
                        </td>
                        <td className='text-center'>
                          <p>
                            {item.type === 'redemption'
                              ? `${item.product.redemption_point} ${formatMessage({
                                  id: 'app.benefits.b-coin',
                                  defaultMessage: 'B Coin',
                                })}`
                              : item.formated_price}
                          </p>
                        </td>
                        <td>
                          <QuantityBox
                            init={item.quantity}
                            onIncrease={(value) => {
                              cartCtx.onUpdateCart?.(item.id, value);
                            }}
                            onDecrease={(value) => {
                              cartCtx.onUpdateCart?.(item.id, value);
                            }}
                          />
                        </td>
                        <td className='text-center'>
                          <p>
                            {item.type === 'redemption'
                              ? `${item.product.redemption_point * item.quantity} ${formatMessage({
                                  id: 'app.benefits.b-coin',
                                  defaultMessage: 'B Coin',
                                })}`
                              : item.formated_total}
                          </p>
                        </td>
                        <td>
                          <Tooltip title='Remove product'>
                            <Button
                              onClick={() => {
                                singleProductRemoveModal.onOpen();
                                singleProductRemoveModal.setExtraInfo<{ id: number }>({
                                  id: item.id,
                                });
                              }}
                              icon={<i className='fal fa-times' />}
                            ></Button>
                          </Tooltip>
                        </td>
                      </tr>
                    )
                )}
              </tbody>
            </table>
          )}
          {isMobile && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type='ghost'
                  onClick={() => {
                    emptyCartModal.onOpen();
                  }}
                >
                  <FormattedMessage id='app.cart.clear_all' defaultMessage='Clear cart' />
                </Button>
              </div>
              <List
                itemLayout='vertical'
                dataSource={cartCtx.cart?.items || []}
                renderItem={(item: CartItemT, index) => (
                  <List.Item key={item.id}>
                    <Button
                      onClick={() => {
                        singleProductRemoveModal.onOpen();
                        singleProductRemoveModal.setExtraInfo<{ id: number }>({
                          id: item.id,
                        });
                      }}
                      style={{ marginBottom: 8 }}
                      icon={<i className='fal fa-trash-alt' />}
                    ></Button>
                    <List.Item.Meta
                      avatar={
                        <Image
                          height={100}
                          width={100}
                          alt='product image'
                          src={item.product.base_image.small_image_url}
                        />
                      }
                      style={{ marginRight: 8 }}
                      title={
                        <div>
                          <Typography.Title level={5} style={{ lineHeight: 1.2, marginBottom: 0 }}>
                            {item.name}
                          </Typography.Title>
                        </div>
                      }
                      description={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>
                            {item.formated_price} x {item.quantity}
                          </span>
                          <span className='table-total'>{item.formated_total}</span>
                        </div>
                      }
                    />
                    <div style={{ paddingRight: 8 }}>
                      <QuantityBox
                        init={item.quantity}
                        onIncrease={(value) => {
                          cartCtx.onUpdateCart?.(item.id, value);
                        }}
                        onDecrease={(value) => {
                          cartCtx.onUpdateCart?.(item.id, value);
                        }}
                      />
                    </div>
                  </List.Item>
                )}
              />
            </div>
          )}
          <div className='flex justify-end mb-4'>
            {!authCtx.isLoggedIn && (
              <button onClick={authCtx.openLoginModal} type='button' className='rounded py-2 px-4 bg-main-400'>
                <FormattedMessage id='app.common.proceed_checkout' defaultMessage='Proceed to Checkout' />
              </button>
            )}
            {authCtx.isLoggedIn && (
              <Link href='/checkout'>
                <a className='rounded py-2 px-4 bg-main-400 hover:text-white'>
                  <FormattedMessage id='app.common.proceed_checkout' defaultMessage='Proceed to Checkout' />
                </a>
              </Link>
            )}
          </div>
        </Fallback>

        <ConfirmDialog
          open={singleProductRemoveModal.open}
          onClose={singleProductRemoveModal.onClose}
          title='Remove item from cart'
          message={formatMessage({
            id: 'app.cart.question.clear_one',
            defaultMessage: 'Are you sure to remove this product from cart',
          })}
          onConfirm={() => {
            if (singleProductRemoveModal.extra.id) {
              singleProductRemoveModal.onClose();
              cartCtx.onRemoveCartItem?.(singleProductRemoveModal.extra.id);
            }
          }}
          onCancel={() => {
            singleProductRemoveModal.onClose();
            singleProductRemoveModal.setExtraInfo({});
          }}
        />

        <ConfirmDialog
          open={emptyCartModal.open}
          onClose={emptyCartModal.onClose}
          title='Remove cart'
          message={formatMessage({
            id: 'app.cart.question.clear_all',
            defaultMessage: 'Are you sure to remove all product from cart',
          })}
          onConfirm={() => {
            emptyCartModal.onClose();
            cartCtx.onEmptyCart?.();
          }}
          onCancel={() => {
            emptyCartModal.onClose();
          }}
        />
      </Container>
    </Layout>
  );
}

export default Cart;
